import { useState } from 'react';
import { Button } from '@/components/ui/button.tsx';
import { api, downloadFile } from '../../../api/client';
import { monthAgoIsoDate, todayIsoDate } from '../../../components/product-analysis/productAnalysisLabels';
import { useTelegram } from '../../../context/TelegramContext';
import { DateField } from '../../../ui/DateField';
import { SETTINGS_DEBUG_HUB } from '../settings-paths';
import {
  SettingsErrorAlert,
  SettingsFormCard,
  SettingsHint,
  SettingsSubPageShell,
} from '../settings-shared';

interface PerformanceStatisticsTable {
  title: string;
  columns: string[];
  rows: string[][];
}

export function SettingsDebugPerformancePage() {
  const { webApp } = useTelegram();
  const [from, setFrom] = useState(monthAgoIsoDate());
  const [to, setTo] = useState(todayIsoDate());
  const [busy, setBusy] = useState<'view' | 'download' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tables, setTables] = useState<PerformanceStatisticsTable[] | null>(null);

  async function handleView() {
    setBusy('view');
    setError(null);
    try {
      const data = await api.post<{ tables: PerformanceStatisticsTable[] }>(
        '/debug/performance/orders-report',
        { from, to, view: true },
      );
      setTables(data.tables ?? []);
      webApp?.HapticFeedback?.notificationOccurred('success');
    } catch (err) {
      setTables(null);
      setError(err instanceof Error ? err.message : 'Не удалось загрузить статистику');
      webApp?.HapticFeedback?.notificationOccurred('error');
    } finally {
      setBusy(null);
    }
  }

  async function handleDownload() {
    setBusy('download');
    setError(null);
    try {
      await downloadFile(
        '/debug/performance/orders-report',
        { from, to },
        `performance-statistics_${from}_${to}.zip`,
      );
      webApp?.HapticFeedback?.notificationOccurred('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось скачать отчёт');
      webApp?.HapticFeedback?.notificationOccurred('error');
    } finally {
      setBusy(null);
    }
  }

  return (
    <SettingsSubPageShell title="Performance API" backTo={SETTINGS_DEBUG_HUB}>
      <SettingsFormCard title="Статистика кампаний">
        <SettingsHint className="mb-3">
          Показы, клики и расход по товарным кампаниям SKU. Каждая кампания — отдельная таблица.
        </SettingsHint>
        <div className="flex flex-col gap-3">
          <DateField id="debug-perf-from" label="Период с" value={from} onChange={setFrom} max={to} />
          <DateField
            id="debug-perf-to"
            label="Период по"
            value={to}
            onChange={setTo}
            min={from}
            max={todayIsoDate()}
          />
          <div className="flex flex-wrap gap-2">
            <Button type="button" disabled={busy != null || !from || !to} onClick={() => void handleView()}>
              {busy === 'view' ? 'Ждём отчёт…' : 'Просмотр статистики'}
            </Button>
            <Button type="button" variant="outline" disabled={busy != null || !from || !to} onClick={() => void handleDownload()}>
              {busy === 'download' ? 'Ждём отчёт…' : 'Скачать архив'}
            </Button>
          </div>
        </div>
      </SettingsFormCard>
      {error && <SettingsErrorAlert>{error}</SettingsErrorAlert>}
      {tables?.length === 0 && <SettingsHint>Ozon не вернул строк по кампаниям за этот период.</SettingsHint>}
      {tables?.map((table) => (
        <SettingsFormCard key={table.title} title={table.title}>
          <div className="w-full overflow-x-auto">
            <table className="w-max border-collapse text-[13px]">
              <thead>
                <tr>
                  {table.columns.map((column) => (
                    <th
                      key={column}
                      className="whitespace-nowrap border-b border-border px-2 py-1.5 text-left"
                    >
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.rows.map((row, rowIndex) => (
                  <tr key={`${table.title}-${rowIndex}`}>
                    {table.columns.map((column, cellIndex) => (
                      <td
                        key={`${column}-${cellIndex}`}
                        className="whitespace-nowrap border-b border-border/60 px-2 py-1.5"
                      >
                        {row[cellIndex] ?? ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </SettingsFormCard>
      ))}
    </SettingsSubPageShell>
  );
}
